/*
Backup ZIP importer.

Reads a ZIP produced by either the PWA exporter or the Android exporter
(same format), inserts captures with dedup on (arbreId, timestamp), and
streams each `photos/<uuid>.jpg` into the IDB photos store.

Refuses any backup whose `meta.json.schemaVersion` is greater than
kCurrentSchemaVersion — bumping versions on either platform is a hard
fence to avoid silently importing rows whose shape we don't understand.

Photos that are referenced by a capture but missing from the ZIP are
non-fatal: the capture still gets inserted, the count of `photosMissing`
is surfaced for the UI to show ("3 photos manquantes").
*/

import { unzipSync, strFromU8 } from 'fflate'
import { captureExists, insertCapture } from '../data/captureRepository'
import { savePhoto } from '../data/photoStore'
import {
	kCurrentSchemaVersion,
	kMetaFilename,
	kCapturesFilename,
	kPhotosDir,
	type BackupMeta,
	type ExportedCapture,
} from './models'

export interface ImportResult
{
	importedCount: number
	skippedDuplicates: number
	photosImported: number
	photosMissing: number
	meta: BackupMeta
}

/*
Import a ZIP file (a Blob/File from a `<input type="file">` selection).
Extracts in memory, validates schema, then ingests captures + photos. No
Room-equivalent enveloping transaction — partial imports are tolerated
(dedup makes them re-runnable).

Optional `inOnProgress` callback narrates the operation for the UI
(Android v1.0.1 spec — label texte + timeout 60 s).
*/
export async function importBackup(inFile: Blob, inOnProgress?: (inLabel: string) => void): Promise<ImportResult>
{
	inOnProgress?.('Lecture du ZIP…')
	const vBytes = new Uint8Array(await inFile.arrayBuffer())  // ZIP file bytes
	const vEntries = unzipSync(vBytes)  // fflate sync extract; OK for personal-scale ZIPs

	const vMetaRaw = vEntries[kMetaFilename]
	if (!vMetaRaw) throw new Error('meta.json absent du backup.')
	const vMeta = JSON.parse(strFromU8(vMetaRaw)) as BackupMeta
	if (vMeta.schemaVersion > kCurrentSchemaVersion)
	{
		throw new Error(`Backup au format v${vMeta.schemaVersion}, ce client lit jusqu'à v${kCurrentSchemaVersion}.`)
	}

	const vCapturesRaw = vEntries[kCapturesFilename]
	if (!vCapturesRaw) throw new Error('captures.json absent du backup.')
	const vCaptures = JSON.parse(strFromU8(vCapturesRaw)) as ExportedCapture[]

	let vImported = 0  // captures actually inserted
	let vSkipped = 0  // duplicates rejected by dedup
	let vPhotos = 0  // photos successfully written to IDB
	let vMissing = 0  // photoFilename referenced but not in the ZIP

	const vTotal = vCaptures.length
	inOnProgress?.(`Import des captures (0/${vTotal})…`)
	let vProcessed = 0
	for (const vCap of vCaptures)
	{
		vProcessed += 1
		if (await captureExists(vCap.arbreId, vCap.timestamp))
		{
			vSkipped += 1
			continue
		}
		await insertCapture(vCap)
		vImported += 1

		if (vCap.photoFilename)
		{
			const vPhotoEntry = vEntries[`${kPhotosDir}${vCap.photoFilename}`]
			if (!vPhotoEntry)
			{
				vMissing += 1
				continue
			}
			const vBlob = new Blob([vPhotoEntry as BlobPart], { type: 'image/jpeg' })
			await savePhoto({ filename: vCap.photoFilename, blob: vBlob, takenAt: vCap.timestamp })
			vPhotos += 1
		}
		if (vProcessed % 5 === 0) inOnProgress?.(`Import des captures (${vProcessed}/${vTotal})…`)  // throttled like the exporter
	}

	return {
		importedCount: vImported,
		skippedDuplicates: vSkipped,
		photosImported: vPhotos,
		photosMissing: vMissing,
		meta: vMeta,
	}
}
